import type { RbacClaim, RbacRole, RbacTier } from '../../models/Rbac';
import { hasDataStudioSeat } from '../../permissions';

/**
 * RBAC v2 predicates for Data Studio (RGI-1042 / RGI-1115).
 *
 * All five predicates short-circuit to `true` for v1 users (no rbac claim)
 * and SuperKudu \u2014 the hard constraint from RGI-1024: legacy users see zero
 * behavioral change at every layer.
 *
 * Inputs are the rbac claim (or null/undefined) plus an optional flag for
 * SuperKudu. Read once at the call site via `store.rbac` + `store.isSuperKudu`.
 */

const TIER_ORDER: Record<RbacTier, number> = {
  starter: 0,
  growth: 1,
  enterprise: 2,
};

type RbacInput = RbacClaim | null | undefined;

interface BypassFlags {
  superKudu?: boolean;
}

export function isV2User(rbac: RbacInput): boolean {
  return !!rbac;
}

export function hasModule(
  rbac: RbacInput,
  moduleId: string,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu) return true;
  if (!rbac) return true;
  return rbac.assignedModules.includes(moduleId);
}

export function tenantPurchased(
  rbac: RbacInput,
  moduleId: string,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu) return true;
  if (!rbac) return true;
  return rbac.purchasedModules.includes(moduleId);
}

export function hasRole(
  rbac: RbacInput,
  requiredRole: RbacRole,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu) return true;
  if (!rbac) return true;
  return rbac.role === requiredRole;
}

export function hasTier(
  rbac: RbacInput,
  minTier: 'growth' | 'enterprise',
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu) return true;
  if (!rbac) return true;
  return TIER_ORDER[rbac.tier] >= TIER_ORDER[minTier];
}

// App-switcher visibility & "(limited access)" semantics. Admin Hub shows for
// Org Admins or module holders, never a read-only user, who has no Admin UI
// whatever modules they hold. Data Studio needs the tenant to have
// purchased it (RGI-3158), then an Org Admin or a module holder; tenants that
// didn't buy it see it greyed out. v1 users / SuperKudu get full access.

export function canSeeAdminHub(
  rbac: RbacInput,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu || !rbac) return true;
  if (rbac.role === 'readOnly') return false;
  return (
    rbac.role === 'orgAdmin' ||
    rbac.assignedModules.includes('data_studio') ||
    rbac.assignedModules.includes('market_analyzer')
  );
}

export function canSeeDataStudio(
  rbac: RbacInput,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu || !rbac) return true;
  // An Org Admin holds every purchased module, but one invited through Admin is
  // stored with `assignedModules: []`, so the role counts on its own. A
  // read-only user only ever gets Market Analyzer, never Data Studio.
  return hasDataStudioSeat(rbac);
}

/** RBAC tenant that didn't buy Data Studio → greyed out in the switcher. */
export function dataStudioNotPurchased(
  rbac: RbacInput,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu || !rbac) return false;
  return !rbac.purchasedModules.includes('data_studio');
}

/** Data Studio standard user (has the module, not an Org Admin) → limited admin nav. */
export function adminHubLimited(
  rbac: RbacInput,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu || !rbac) return false;
  return (
    rbac.assignedModules.includes('data_studio') && rbac.role !== 'orgAdmin'
  );
}
